import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AutomationsResponse } from '../api/automations.js';
import {
  getAutomations,
  getAutomationHistory,
  getAutomationsProgress,
  triggerAutomation,
  stopAutomation,
} from '../api/automations.js';

export function useAutomations() {
  const queryClient = useQueryClient();
  const hadRunningRef = useRef(false);

  const query = useQuery({
    queryKey: ['automations'],
    queryFn: getAutomations,
    refetchInterval: (query) => {
      const hasRunning = query.state.data?.automations.some((c) => c.status === 'running');
      return hasRunning ? 5_000 : 3_000_000;
    },
  });

  const hasRunning = query.data?.automations.some((automation) => automation.status === 'running') ?? false;

  useEffect(() => {
    if (hadRunningRef.current && !hasRunning) {
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    }
    hadRunningRef.current = hasRunning;
  }, [hasRunning, queryClient]);

  return query;
}

export function useAutomationsProgress(enabled: boolean) {
  return useQuery({
    queryKey: ['automations', 'progress'],
    queryFn: getAutomationsProgress,
    enabled,
    refetchInterval: enabled ? 5_000 : false,
  });
}

export function useAutomationHistory(automationId: string | null) {
  return useQuery({
    queryKey: ['automations', automationId, 'history'],
    queryFn: () => getAutomationHistory(automationId!),
    enabled: automationId !== null,
  });
}

export function useTriggerAutomation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: triggerAutomation,
    onSuccess: (_data, automationId) => {
      // Optimistically mark the automation as running so the UI shows the Stop button
      // and the refetchInterval switches to 5s polling immediately.
      queryClient.setQueryData<AutomationsResponse>(['automations'], (old) => {
        if (!old) return old;
        const automations = old.automations.map((a) => (a.id === automationId ? { ...a, status: 'running' as const } : a));
        return {
          ...old,
          automations,
          summary: {
            ...old.summary,
            running: automations.filter((a) => a.status === 'running').length,
            healthy: automations.filter((a) => a.status === 'healthy').length,
          },
        };
      });
      // Don't invalidate immediately — the optimistic update already set running
      // status and switched to 5s polling. An immediate refetch could overwrite
      // the optimistic state if pg-boss hasn't persisted the job row yet.
    },
  });
}

export function useStopAutomation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: stopAutomation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automations'] });
    },
  });
}
